// src/utils/resizeImage.js
// Shrinks/compresses an image File in the browser BEFORE uploading to Firebase
// Storage. Returns a new JPEG File capped to `maxDim` px on its longest side and
// compressed toward `targetBytes`. On any failure (e.g. a format the browser
// can't decode), it safely returns the original file so the upload still works.

export async function resizeImage(
  file,
  {
    maxDim = 1024,
    targetBytes = 200 * 1024,
    minQuality = 0.5,
    mimeType = 'image/jpeg',
  } = {}
) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (e) {
    try {
      bitmap = await createImageBitmap(file);
    } catch (e2) {
      return file;
    }
  }

  const { width, height } = bitmap;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  if (bitmap.close) bitmap.close();

  let quality = 0.85;
  let blob = await canvasToBlob(canvas, mimeType, quality);
  while (blob && blob.size > targetBytes && quality > minQuality) {
    quality = Math.max(minQuality, Math.round((quality - 0.1) * 100) / 100);
    blob = await canvasToBlob(canvas, mimeType, quality);
  }

  if (!blob) return file;

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, {
    type: mimeType,
    lastModified: Date.now(),
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((b) => resolve(b), type, quality);
    } else {
      try {
        const dataUrl = canvas.toDataURL(type, quality);
        const bytes = atob(dataUrl.split(',')[1]);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        resolve(new Blob([arr], { type }));
      } catch (e) {
        resolve(null);
      }
    }
  });
}
